import { cn } from "@tcg/ui";
import { CheckCircle2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useYen } from "@/components/MoneyText";
import type { TaxFreeVerification } from "@/lib/types";

export function reasonCode(reason: string): string {
  return reason.replace(/^taxfree\.reason\./, "");
}

/**
 * 2) 結果 (design/05 §3.3): ✅ / ❌ with the reasons in plain words, the ¥5,000 check
 * (「対象品 税抜 ¥21,364 ≥ ¥5,000 ✅ (本日合算含む)」) and the buyer's name to compare with the
 * passport (shown in full only in this answer — masked afterwards).
 */
export function EligibilityResult({ result }: { result: TaxFreeVerification }) {
  const { t } = useTranslation();
  const yen = useYen();
  const check = result.thresholdCheck;
  return (
    <div className="flex flex-col gap-3">
      <p
        role={result.eligible ? "status" : "alert"}
        data-testid="eligibility"
        data-eligible={result.eligible}
        className={cn(
          "flex items-center gap-3 rounded-md border-2 p-3 text-xl font-semibold",
          result.eligible
            ? "border-pos-ok bg-pos-ok/10 text-pos-ok"
            : "border-status-problem bg-background text-foreground",
        )}
      >
        {result.eligible ? (
          <CheckCircle2 aria-hidden className="size-8 shrink-0" />
        ) : (
          <XCircle aria-hidden className="size-8 shrink-0 text-pos-problem" />
        )}
        {result.eligible ? t("taxfree.eligible") : t("taxfree.not_eligible")}
      </p>
      {result.reasons.length > 0 ? (
        <ul className="flex list-disc flex-col gap-1 pl-6 text-lg" data-testid="reasons">
          {result.reasons.map((reason) => (
            <li key={reason}>
              {t(`taxfree.reason_${reasonCode(reason)}`, {
                defaultValue: t("taxfree.reason_other", { code: reasonCode(reason) }),
              })}
            </li>
          ))}
        </ul>
      ) : null}
      <dl className="grid grid-cols-2 gap-2 rounded-md border p-3">
        <dt className="text-muted-foreground">{t("taxfree.buyer")}</dt>
        <dd className="font-mono text-lg">{result.buyer.name}</dd>
        {result.buyer.passportNumberMasked ? (
          <>
            <dt className="text-muted-foreground">{t("taxfree.passport_number")}</dt>
            <dd className="font-mono">{result.buyer.passportNumberMasked}</dd>
          </>
        ) : null}
        <dt className="text-muted-foreground">{t("taxfree.threshold")}</dt>
        <dd data-testid="threshold" className={check.ok ? "text-pos-ok" : "text-pos-problem"}>
          {t(check.ok ? "taxfree.threshold_ok" : "taxfree.threshold_ng", {
            amount: yen(check.total),
            min: yen(check.min),
          })}
          {check.priorSameDayExclTax > 0 ? ` ${t("taxfree.threshold_prior")}` : ""}
        </dd>
      </dl>
    </div>
  );
}
